import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import FujiModal from '../components/fuji-modal/FujiModal';
import { showNotificationDialog, isAfterExpired, isInStartEnd } from '../utils';
import { CacheManager } from '../utils/CacheManager/CacheManager';
import useR05Api from '../api/useR05Api';
import { TIMEOUT_DURATION, USAGE_TICKET_STATUS } from '../utils/Constants';
import { DistributionQrCode, ErrorType, OrderTicket } from 'types';

const useDisplayTicket = () => {
    const [isLoading, setLoading] = useState(false);
    const [smallLoading, setSmallLoading] = useState(false);
    const [orderTicket, setOrderTicket] = useState<OrderTicket | DistributionQrCode>();
    const [timeOverModal, setTimeOverModal] = useState(false);
    const [displayEndModal, setDisplayEndModal] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { fetchOrderDetail, fetchDistributionQrCode: getDistributionQrCode } = useR05Api();

    /**
     * fetch errorの対応
     */
    const showFetchError = useCallback(
        (error: ErrorType) => {
            const showError = (text: string) => {
                showNotificationDialog('error', 'fetchTicketDisplay', t('notification.title'), text);
            };

            switch (error.errorType) {
                case 'offline':
                case 'timeout':
                default:
                    showError(t('notification.network-failed'));
                    break;
            }
        },
        [t],
    );

    /**
     * チケットを取得する。（オンライン/低速/オフライン)
     */
    const fetchOrderTicket = useCallback(
        async (orderId: string, orderTicketId: string) => {
            setLoading(true);

            const cacheName = 'bought_ticket_detail_cache';
            const cacheUrl = `/bought_ticket_detail/?orderId=${orderId}&orderTicketId=${orderTicketId}`;
            const boughtTicketDetailCache = new CacheManager(cacheName, cacheUrl);

            // オンライン時のfetch
            const fetchOnline = async () => {
                const response = await fetchOrderDetail({
                    orderId: orderId,
                    orderTicketId: orderTicketId,
                });
                await boughtTicketDetailCache.saveToCache(response);
                setOrderTicket({
                    ...response,
                    ...response.comboItems[0],
                });
            };

            // オフライン or 低速時のfetch
            const fetchOffline = async () => {
                const response = await boughtTicketDetailCache.readFromCache();
                setOrderTicket({ ...response, ...response.comboItems[0] });
            };

            // キャッシュのデータをまず表示、エラー時には何もしない
            try {
                await fetchOffline();
            } catch {}
            setLoading(false);

            // オンライン時は、ポップアップのローディングを出して、チケット一覧を更新する
            if (navigator.onLine) {
                setSmallLoading(true);
                try {
                    await fetchOnline();
                } catch (error) {
                    showFetchError(error as ErrorType);
                }
                setSmallLoading(false);
            }
        },
        [fetchOrderDetail, showFetchError],
    );

    /**
     * チケット表示設定を更新する
     * キャッシュしているだけっぽいね
     */
    const setTicketSettings = useCallback(
        async (orderTicket: OrderTicket | DistributionQrCode) => {
            const backgroundImage1 = orderTicket?.backgroundImage1 ?? null;
            const backgroundImage2 = orderTicket?.backgroundImage2 ?? null;

            const cacheManager = new CacheManager('backgroundImages', '');
            // 画像をキャッシュする。キャッシュされた画像を返す。
            const cashingImage = async (imageUrl: string, callback: (params: any) => void) => {
                const timeoutId = setTimeout(() => {
                    callback(null);
                }, 5000);
                try {
                    const cacheUrl = await cacheManager.readImage(imageUrl);
                    callback(cacheUrl);
                    clearTimeout(timeoutId);
                } catch (error: any) {
                    if (error.message === 'No data in cache') {
                        // console.log('キャッシュからの画像取得に失敗しました');
                    } else {
                        console.error(error);
                    }
                }
                if (navigator.onLine) {
                    try {
                        const image = await fetch(imageUrl);
                        if (!image.ok) {
                            throw new Error('Failed to fetch image');
                        }
                        const cacheUrl = await cacheManager.saveImage(imageUrl, image);
                        callback(cacheUrl);
                        clearTimeout(timeoutId);
                    } catch (error) {
                        console.error(error);
                        callback(null);
                    }
                }
            };

            const renewedOrderTicket = { ...orderTicket };
            if (backgroundImage1) {
                await cashingImage(backgroundImage1, (newImage: string) => {
                    renewedOrderTicket.backgroundImage1 = newImage;
                });
            }
            if (backgroundImage2) {
                await cashingImage(backgroundImage2, (newImage: string) => {
                    renewedOrderTicket.backgroundImage2 = newImage;
                });
            }

            /**
             * 更新内容をreduxへ反映
             */
            dispatch({
                type: 'SET_CURRENT_TICKET',
                payload: renewedOrderTicket,
            });
        },
        [dispatch],
    );

    /**
     * 表示するチケットのデータが更新された時
     */
    useEffect(() => {
        let checkIntervalId: NodeJS.Timeout;
        if (orderTicket) {
            dispatch({
                type: 'CHANGE_IS_ACTIVE_TICKET_SHOWN',
                payload: true,
            });
            void setTicketSettings(orderTicket);

            const { availableStartAt, displayEndAt, orderUsageTickets } = orderTicket ?? {};

            const orderUsageTicket = orderUsageTickets?.[0];
            const expiredAt = orderUsageTicket?.expiredAt;
            const orderUsageStatus =
                orderUsageTicket && 'status' in orderUsageTicket ? orderUsageTicket.status : undefined;

            // 使用終了のチェック
            if (orderUsageStatus === USAGE_TICKET_STATUS.USED) {
                setTimeOverModal(true);
            }

            // 1秒ごとにチケットの状態をチェックする
            checkIntervalId = setInterval(() => {
                // 表示可能時間のチェック
                if (
                    !isInStartEnd({
                        startTime: availableStartAt,
                        endTime: displayEndAt,
                    })
                ) {
                    setDisplayEndModal(true);
                }
                // 有効期限のチェック
                if (isAfterExpired(expiredAt)) {
                    setTimeOverModal(true);
                }
            }, 1000);
        }
        return () => {
            clearInterval(checkIntervalId);
            /**
             * チケットタブを非表示にした時の挙動
             */
            dispatch({
                type: 'CHANGE_IS_ACTIVE_TICKET_SHOWN',
                payload: false,
            });
            dispatch({
                type: 'REMOVE_CURRENT_TICKET',
            });
        };
    }, [dispatch, orderTicket, setTicketSettings]);

    /**
     * 有効期限が切れた時の挙動
     */
    const handleAfterTimeOver = useCallback(() => {
        dispatch({
            type: 'REMOVE_CURRENT_TICKET',
        });
        setTimeOverModal(false);
        navigate('/bought');
    }, [dispatch, navigate]);

    /**
     * 表示可能時間が過ぎた時の挙動
     */
    const handleAfterDisplayEnd = useCallback(() => {
        dispatch({
            type: 'REMOVE_CURRENT_TICKET',
        });
        setDisplayEndModal(false);
        navigate('/bought');
    }, [dispatch, navigate]);

    const TimeOverModal = useCallback(() => {
        return (
            <FujiModal
                isOpen={timeOverModal}
                onSubmit={handleAfterTimeOver}
                txtSubmit={'OK'}
                body={t('ticket-display.has-expired')}
            />
        );
    }, [handleAfterTimeOver, t, timeOverModal]);

    const DisplayEndModal = useCallback(() => {
        return (
            <FujiModal
                isOpen={displayEndModal}
                onSubmit={handleAfterDisplayEnd}
                txtSubmit={'OK'}
                body={t('ticket-display.display-end')}
            />
        );
    }, [displayEndModal, handleAfterDisplayEnd, t]);

    /**
     * 分配QRコードを取得する
     */
    const fetchDistributionQrCode = useCallback(async (requestParams: { distributionCode: string }) => {
        try {
            setLoading(true);
            const response = await getDistributionQrCode(requestParams, TIMEOUT_DURATION);
            const data = {
                ...response,
                ...response?.orderTicket,
                ...response?.orderTicket?.order,
                ...response?.orderTicket?.orderUsageTickets?.[0]?.reservationInfo,
            } as DistributionQrCode;
            setOrderTicket(data);
            void setTicketSettings(data);
            setLoading(false);
            return data;
        } catch (error) {
            showFetchError(error as ErrorType);
            setLoading(false);
        }
    }, []);

    return {
        fetchOrderTicket,
        isLoading,
        smallLoading,
        orderTicket,
        TimeOverModal,
        DisplayEndModal,
        fetchDistributionQrCode,
    };
};

export default useDisplayTicket;
